import { useEffect, useState } from "react";
import { FiX, FiPlay, FiPause, FiSkipForward } from "react-icons/fi";
import { useTheme } from "../../../context/ThemeContext";
import CameraStreamWebRTC from "../../../components/CameraStreamWebRTC";

/**
 * TourMode - Fixed Version
 * ========================================
 * Shows cameras in fullscreen overlay
 * Auto-rotates with progress indicator
 */
export default function TourMode({ cameras, onClose, updateStreamStatus }) {
  const { theme } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [intervalDuration, setIntervalDuration] = useState(10); // seconds (renamed to avoid conflict)
  const [progress, setProgress] = useState(0);

  // Auto-advance through cameras
  useEffect(() => {
    if (paused || cameras.length === 0) return;

    // Reset progress when unpausing or changing camera
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          return 100;
        }
        return prev + (100 / (intervalDuration * 10)); // Update every 100ms
      });
    }, 100);

    const advanceTimer = setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % cameras.length);
    }, intervalDuration * 1000);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(advanceTimer);
    };
  }, [currentIndex, paused, intervalDuration, cameras.length]);

  const skipNext = () => {
    setCurrentIndex((prev) => (prev + 1) % cameras.length);
    setProgress(0);
  };

  const skipPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + cameras.length) % cameras.length);
    setProgress(0);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        skipNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        skipPrevious();
      } else if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        setPaused(!paused);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [paused, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentCamera = cameras[currentIndex];

  if (!currentCamera) return null;

  return (
    <>
      {/* Fullscreen Overlay */}
      <div className="fixed inset-0 z-50 bg-black">
        {/* Camera Stream - Full Screen */}
        <div className="absolute inset-0">
          <CameraStreamWebRTC
            key={currentCamera.id}
            path={`cam${currentCamera.id}`}
            baseUrl="https://vision.viewmax.net"
            muted
            autoplay
            isActive={currentCamera.is_active !== false}
            cameraName={currentCamera.name}
            className="w-full h-full"
            onFallback={() => {
              console.warn(`Tour: Camera ${currentCamera.id} failed`);
              updateStreamStatus?.(currentCamera.id, "offline");
            }}
          />
        </div>

        {/* Camera Info Overlay - Top */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <span className="text-white font-semibold text-sm uppercase tracking-wider">
                  Tour Mode Active
                </span>
              </div>
              <h2 className="text-white text-3xl font-bold mb-2">
                {currentCamera.name || "Unnamed Camera"}
              </h2>
              <div className="flex items-center gap-4 text-white/80 text-sm">
                <span>Camera {currentIndex + 1} of {cameras.length}</span>
                {currentCamera.location && (
                  <>
                    <span>•</span>
                    <span>{currentCamera.location}</span>
                  </>
                )}
                {currentCamera.ip_address && (
                  <>
                    <span>•</span>
                    <span>{currentCamera.ip_address}</span>
                  </>
                )}
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="p-3 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md text-white transition-all"
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-6 h-1.5 rounded-full bg-white/20 overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Controls - Bottom Center */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
          <div className="max-w-2xl mx-auto">
            {/* Main Controls */}
            <div className="flex items-center justify-center gap-4 mb-4">
              {/* Previous */}
              <button
                onClick={skipPrevious}
                className="p-4 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white transition-all"
                title="Previous camera"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Pause/Play */}
              <button
                onClick={() => setPaused(!paused)}
                className="p-5 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-xl"
                title={paused ? "Resume tour" : "Pause tour"}
              >
                {paused ? <FiPlay size={28} /> : <FiPause size={28} />}
              </button>

              {/* Next */}
              <button
                onClick={skipNext}
                className="p-4 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white transition-all"
                title="Next camera"
              >
                <FiSkipForward size={24} />
              </button>
            </div>

            {/* Interval Selector */}
            <div className="flex items-center justify-center gap-2">
              <span className="text-white/80 text-sm">Interval:</span>
              {[5, 10, 30, 60].map(sec => (
                <button
                  key={sec}
                  onClick={() => {
                    setIntervalDuration(sec);
                    setProgress(0);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    intervalDuration === sec
                      ? "bg-blue-600 text-white"
                      : "bg-white/10 text-white/80 hover:bg-white/20"
                  }`}
                >
                  {sec}s
                </button>
              ))}
            </div>

            {/* Camera Thumbnails Navigation */}
            <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {cameras.map((cam, idx) => (
                <button
                  key={cam.id}
                  onClick={() => {
                    setCurrentIndex(idx);
                    setProgress(0);
                  }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    idx === currentIndex
                      ? "bg-blue-600 text-white"
                      : "bg-white/10 text-white/80 hover:bg-white/20"
                  }`}
                  title={cam.name}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Keyboard Hint - Bottom Left */}
        <div className="absolute bottom-6 left-6 text-white/60 text-xs space-y-1">
          <div>Press <kbd className="px-2 py-1 rounded bg-white/20 font-mono">ESC</kbd> to exit</div>
          <div>Press <kbd className="px-2 py-1 rounded bg-white/20 font-mono">←</kbd> <kbd className="px-2 py-1 rounded bg-white/20 font-mono">→</kbd> to navigate</div>
          <div>Press <kbd className="px-2 py-1 rounded bg-white/20 font-mono">SPACE</kbd> to pause/play</div>
        </div>
      </div>
    </>
  );
}
